import {
    getTaskTypesService,
    getDashboardService,
    getTransactionsService,
    getLeaderboardService,
    createTaskTypeService
} from "../services/kpi";

export const kpi = {
    state: {
        taskTypes: [],
        dashboard: null,
        transactions: [],
        leaderboard: [],
        trend: [],
        myWorks: null,
        categoryBreakdown: [],
        teamReview: null,
        heatmap: [],
        eligibleUsers: [],
        workDetail: null,
        taskHistory: [],
        taskComments: [],
        kpiProjects: [],
        upcomingRecurring: [],
        upcomingRecurringCounts: { active: 0, paused: 0, cancelled: 0, all: 0 },
        upcomingRecurringPagination: null,
        adminDashboard: null,
        activityLog: [],
        monthlyTargets: [],
        recurringDetail: null,
        loading: false,
    },
    reducers: {
        setLoading: (state, loading) => ({
            ...state,
            loading
        }),
        setMonthlyTargets: (state, monthlyTargets) => ({
            ...state,
            monthlyTargets
        }),
        setRecurringDetail: (state, recurringDetail) => ({
            ...state,
            recurringDetail
        }),
        setTaskTypes: (state, taskTypes) => ({
            ...state,
            taskTypes
        }),
        setDashboard: (state, dashboard) => ({
            ...state,
            dashboard
        }),
        setTransactions: (state, transactions) => ({
            ...state,
            transactions
        }),
        setLeaderboard: (state, leaderboard) => ({
            ...state,
            leaderboard
        }),
        setTrend: (state, trend) => ({
            ...state,
            trend
        }),
        setMyWorks: (state, myWorks) => ({
            ...state,
            myWorks
        }),
        setCategoryBreakdown: (state, categoryBreakdown) => ({
            ...state,
            categoryBreakdown
        }),
        setTeamReview: (state, teamReview) => ({
            ...state,
            teamReview
        }),
        setHeatmap: (state, heatmap) => ({
            ...state,
            heatmap
        }),
        setEligibleUsers: (state, eligibleUsers) => ({
            ...state,
            eligibleUsers
        }),
        setWorkDetail: (state, workDetail) => ({
            ...state,
            workDetail
        }),
        setTaskHistory: (state, taskHistory) => ({
            ...state,
            taskHistory
        }),
        setTaskComments: (state, taskComments) => ({
            ...state,
            taskComments
        }),
        setKpiProjects: (state, kpiProjects) => ({
            ...state,
            kpiProjects
        }),
        setUpcomingRecurring: (state, upcomingRecurring) => ({
            ...state,
            upcomingRecurring
        }),
        setUpcomingRecurringCounts: (state, upcomingRecurringCounts) => ({
            ...state,
            upcomingRecurringCounts
        }),
        setUpcomingRecurringPagination: (state, upcomingRecurringPagination) => ({
            ...state,
            upcomingRecurringPagination
        }),
        setAdminDashboard: (state, adminDashboard) => ({
            ...state,
            adminDashboard
        }),
        setActivityLog: (state, activityLog) => ({
            ...state,
            activityLog
        })
    },
    effects: (dispatch) => ({
        async getTaskTypes() {
            this.setLoading(true);
            try {
                const response = await getTaskTypesService();
                if (response?.data?.data) {
                    this.setTaskTypes(response.data.data);
                }
            } catch (err) {
                console.error("Error fetching task types", err);
            } finally {
                this.setLoading(false);
            }
        },
        async getDashboard(params = {}) {
            this.setLoading(true);
            try {
                const response = await getDashboardService(params);
                const dashData = response?.data?.data || response?.data;
                if (dashData) {
                    this.setDashboard(dashData);
                    if (Array.isArray(dashData.leaderboard) && dashData.leaderboard.length > 0) {
                        this.setLeaderboard(dashData.leaderboard);
                    }
                    if (params.includeTrend && Array.isArray(dashData.trend) && dashData.trend.length > 0) {
                        this.setTrend(dashData.trend);
                    }
                }
            } catch (err) {
                console.error("Error fetching dashboard", err);
            } finally {
                this.setLoading(false);
            }
        },
        async getTransactions() {
            this.setLoading(true);
            try {
                const response = await getTransactionsService();
                if (response?.data?.data) {
                    this.setTransactions(response.data.data);
                }
            } catch (err) {
                console.error("Error fetching transactions", err);
            } finally {
                this.setLoading(false);
            }
        },
        async getLeaderboard() {
            this.setLoading(true);
            try {
                const response = await getLeaderboardService();
                const list = response?.data?.data || (Array.isArray(response?.data) ? response.data : []);
                this.setLeaderboard(Array.isArray(list) ? list : []);
            } catch (err) {
                console.error("Error fetching leaderboard", err);
            } finally {
                this.setLoading(false);
            }
        },
        async getTrend(params) {
            this.setLoading(true);
            try {
                const { getTrendService } = await import("../services/kpi");
                const response = await getTrendService(params);
                if (response?.data?.data) {
                    this.setTrend(response.data.data);
                }
            } catch (err) {
                console.error("Error fetching trend", err);
            } finally {
                this.setLoading(false);
            }
        },
        async getMyWorks(params = {}) {
            const hideLoading = params?.hideLoading;
            if (!hideLoading) this.setLoading(true);
            try {
                const { getMyWorksService } = await import("../services/kpi");
                const response = await getMyWorksService(params);
                if (response?.data) {
                    this.setMyWorks(response.data.data || response.data);
                }
            } catch (err) {
                console.error("Error fetching my works", err);
            } finally {
                if (!hideLoading) this.setLoading(false);
            }
        },
        async getMyWorkDetail(payload) {
            const id = typeof payload === 'object' ? payload.id : payload;
            const hideLoading = typeof payload === 'object' ? payload.hideLoading : false;
            if (!hideLoading) this.setLoading(true);
            try {
                const { getMyWorkDetailService } = await import("../services/kpi");
                const response = await getMyWorkDetailService(id);
                if (response?.data) {
                    this.setWorkDetail(response.data.data || response.data);
                    return response.data;
                }
            } catch (err) {
                console.error("Error fetching my work detail", err);
                const status = err?.response?.status || err?.status || 403;
                this.setWorkDetail({
                    errorStatus: status,
                    errorMessage: err?.response?.data?.message || err?.message || (status === 403 ? "Forbidden" : "Not Found")
                });
                throw err;
            } finally {
                if (!hideLoading) this.setLoading(false);
            }
        },
        async getCategoryBreakdown(params) {
            this.setLoading(true);
            try {
                const { getCategoryBreakdownService } = await import("../services/kpi");
                const response = await getCategoryBreakdownService(params);
                const list = response?.data?.data || response?.data?.categories || response?.data?.category_breakdown || (Array.isArray(response?.data) ? response.data : null);
                if (list) {
                    this.setCategoryBreakdown(list);
                }
            } catch (err) {
                console.error("Error fetching category breakdown", err);
            } finally {
                this.setLoading(false);
            }
        },
        async getTeamReview(params) {
            this.setLoading(true);
            try {
                const { getTeamReviewService } = await import("../services/kpi");
                const response = await getTeamReviewService(params);
                if (response?.data?.data) {
                    this.setTeamReview(response.data.data);
                }
            } catch (err) {
                console.error("Error fetching team review", err);
            } finally {
                this.setLoading(false);
            }
        },
        async getHeatmap(params) {
            this.setLoading(true);
            try {
                const { getHeatmapService } = await import("../services/kpi");
                const response = await getHeatmapService(params);
                if (response?.data?.data) {
                    this.setHeatmap(response.data.data);
                }
            } catch (err) {
                console.error("Error fetching heatmap", err);
            } finally {
                this.setLoading(false);
            }
        },
        async getEligibleUsers(params) {
            this.setLoading(true);
            try {
                const { getEligibleUsersService } = await import("../services/kpi");
                const response = await getEligibleUsersService(params);
                if (response?.data?.data) {
                    this.setEligibleUsers(response.data.data);
                }
            } catch (err) {
                console.error("Error fetching eligible users", err);
            } finally {
                this.setLoading(false);
            }
        },
        async createWork(payload) {
            this.setLoading(true);
            try {
                const { projectAddTaskCreationService } = await import("../services/projectDetails");
                const response = await projectAddTaskCreationService(payload);
                this.getMyWorks({ status: "all" });
                return response?.data || response;
            } catch (err) {
                console.error("Error creating work", err);
                throw err;
            } finally {
                this.setLoading(false);
            }
        },
        async updateWorkStatus(payload) {
            const hideLoading = payload?.hideLoading;
            if (!hideLoading) this.setLoading(true);
            try {
                const { projectTaskUpdationService } = await import("../services/projectDetails");
                const response = await projectTaskUpdationService(payload);
                return response;
            } catch (err) {
                console.error("Error updating work status", err);
                throw err;
            } finally {
                if (!hideLoading) this.setLoading(false);
            }
        },
        async addComment(payload) {
            try {
                const { projectTaskCommentCreationService } = await import("../services/projectDetails");
                const response = await projectTaskCommentCreationService(payload);
                return response;
            } catch (err) {
                console.error("Error adding comment", err);
                throw err;
            }
        },
        async getTaskComments(params) {
            try {
                const { getTaskCommentsService } = await import("../services/projectDetails");
                const response = await getTaskCommentsService(params);
                if (response?.data) {
                    const commentsData = response.data.comments_data || response.data.comments || response.data.data || response.data;
                    this.setTaskComments(Array.isArray(commentsData) ? commentsData : []);
                    return response.data;
                }
            } catch (err) {
                console.error("Error fetching task comments", err);
            }
        },
        async getTaskHistory(taskId) {
            try {
                const { tasksHistoryListService } = await import("../services/projectDetails");
                const id = typeof taskId === 'object' ? (taskId.taskId || taskId.task_id || taskId.id) : taskId;
                const response = await tasksHistoryListService({ taskId: id, task_id: id, tw_task_id: id });
                if (response?.data) {
                    const resObj = response.data;
                    const history = resObj.result || resObj.data || resObj.history || resObj;
                    const finalArr = Array.isArray(history) ? history : (Array.isArray(history?.data) ? history.data : []);
                    this.setTaskHistory(finalArr);
                    return finalArr;
                }
            } catch (err) {
                console.error("Error fetching task history", err);
            }
        },
        async createTaskType(payload) {
            this.setLoading(true);
            try {
                const response = await createTaskTypeService(payload);
                this.getTaskTypes();
                return response;
            } catch (err) {
                console.error("Error creating task type", err);
                throw err;
            } finally {
                this.setLoading(false);
            }
        },
        async updateTaskType({ id, payload }) {
            this.setLoading(true);
            try {
                const { updateTaskTypeService } = await import("../services/kpi");
                const response = await updateTaskTypeService(id, payload);
                this.getTaskTypes();
                return response;
            } catch (err) {
                console.error("Error updating task type", err);
                throw err;
            } finally {
                this.setLoading(false);
            }
        },
        async deleteTaskType(id) {
            this.setLoading(true);
            try {
                const { updateTaskTypeService } = await import("../services/kpi");
                const response = await updateTaskTypeService(id, { is_active: false, is_deleted: true });
                this.getTaskTypes();
                return response;
            } catch (err) {
                console.error("Error deleting task type", err);
                throw err;
            } finally {
                this.setLoading(false);
            }
        },
        async editRecurrence({ id, payload }) {
            this.setLoading(true);
            try {
                const { editRecurrenceService } = await import("../services/kpi");
                const response = await editRecurrenceService(id, payload);
                return response;
            } catch (err) {
                console.error("Error editing recurrence", err);
                throw err;
            } finally {
                this.setLoading(false);
            }
        },
        async softDeleteWork(taskId) {
            this.setLoading(true);
            try {
                const { softDeleteWorkService } = await import("../services/kpi");
                const response = await softDeleteWorkService(taskId);
                this.getMyWorks({ status: "all" });
                return response;
            } catch (err) {
                console.error("Error soft deleting work", err);
                throw err;
            } finally {
                this.setLoading(false);
            }
        },
        async hardDeleteWork(taskId) {
            this.setLoading(true);
            try {
                const { hardDeleteWorkService } = await import("../services/kpi");
                const response = await hardDeleteWorkService(taskId);
                this.getMyWorks({ status: "all" });
                return response;
            } catch (err) {
                console.error("Error hard deleting work", err);
                throw err;
            } finally {
                this.setLoading(false);
            }
        },
        async getKPIProjects(params = {}) {
            const hideLoading = params?.hideLoading;
            if (!hideLoading) this.setLoading(true);
            try {
                const { getKPIProjectsHierarchyService } = await import("../services/kpi");
                const response = await getKPIProjectsHierarchyService(params);
                if (response?.data) {
                    const data = response.data.data || response.data || [];
                    this.setKpiProjects(Array.isArray(data) ? data : []);
                }
            } catch (err) {
                console.error("Error fetching KPI projects hierarchy", err);
            } finally {
                if (!hideLoading) this.setLoading(false);
            }
        },
        async createProjectGroup(payload) {
            this.setLoading(true);
            try {
                const { createProjectGroupService } = await import("../services/kpi");
                const response = await createProjectGroupService(payload);
                return response;
            } catch (err) {
                console.error("Error creating project group", err);
                throw err;
            } finally {
                this.setLoading(false);
            }
        },
        async editWork(payload) {
            const hideLoading = payload?.hideLoading;
            if (!hideLoading) this.setLoading(true);
            try {
                const { projectTaskUpdationService } = await import("../services/projectDetails");
                const response = await projectTaskUpdationService(payload);
                this.getMyWorks({ status: "all" });
                return response;
            } catch (err) {
                console.error("Error editing work", err);
                throw err;
            } finally {
                if (!hideLoading) this.setLoading(false);
            }
        },
        async editProjectGroup(payload) {
            this.setLoading(true);
            try {
                const { projectGroupUpdationService } = await import("../services/projectDetails");
                const response = await projectGroupUpdationService(payload);
                return response;
            } catch (err) {
                console.error("Error editing project group", err);
                throw err;
            } finally {
                this.setLoading(false);
            }
        },
        async softDeleteProjectGroup(groupId) {
            this.setLoading(true);
            try {
                const { projectTaskGroupService } = await import("../services/projectDetails");
                const response = await projectTaskGroupService({ group_id: groupId });
                this.getMyWorks({ status: "all" });
                return response;
            } catch (err) {
                console.error("Error soft deleting project group", err);
                throw err;
            } finally {
                this.setLoading(false);
            }
        },
        async getUpcomingRecurring(params = {}) {
            this.setLoading(true);
            try {
                const { getUpcomingRecurringService } = await import("../services/kpi");
                const response = await getUpcomingRecurringService(params);
                const list = response?.data?.data || (Array.isArray(response?.data) ? response.data : (response?.data?.recurring || []));
                this.setUpcomingRecurring(Array.isArray(list) ? list : []);

                const counts = response?.data?.counts || null;
                if (counts) {
                    const active = Number(counts.active ?? 0);
                    const paused = Number(counts.paused ?? 0);
                    const cancelled = Number(counts.cancelled ?? 0);
                    const all = counts.all !== undefined && counts.all !== null
                        ? Number(counts.all)
                        : (active + paused + cancelled);
                    this.setUpcomingRecurringCounts({ active, paused, cancelled, all });
                }

                const pagination = response?.data?.pagination || null;
                if (pagination) {
                    this.setUpcomingRecurringPagination(pagination);
                }

                return response;
            } catch (err) {
                console.error("Error fetching upcoming recurring tasks", err);
            } finally {
                this.setLoading(false);
            }
        },
        async getAdminDashboard(params = {}) {
            this.setLoading(true);
            try {
                const { getAdminDashboardService } = await import("../services/kpi");
                const response = await getAdminDashboardService(params);
                const data = response?.data?.data || response?.data || null;
                this.setAdminDashboard(data);
                return response;
            } catch (err) {
                console.error("Error fetching admin dashboard", err);
            } finally {
                this.setLoading(false);
            }
        },
        async getActivityLog(params = {}) {
            this.setLoading(true);
            try {
                const { getActivityLogService } = await import("../services/kpi");
                const response = await getActivityLogService(params);
                const data = response?.data?.data || response?.data || [];
                this.setActivityLog(data);
                return response;
            } catch (err) {
                console.error("Error fetching activity log", err);
            } finally {
                this.setLoading(false);
            }
        },
        async getMonthlyTargets(params = {}) {
            this.setLoading(true);
            try {
                const { getMonthlyTargetsService } = await import("../services/kpi");
                const response = await getMonthlyTargetsService(params);
                const list = response?.data?.data || (Array.isArray(response?.data) ? response.data : []);
                this.setMonthlyTargets(Array.isArray(list) ? list : []);
                return response;
            } catch (err) {
                console.error("Error fetching monthly targets", err);
            } finally {
                this.setLoading(false);
            }
        },
        async createMonthlyTarget(payload) {
            this.setLoading(true);
            try {
                const { createMonthlyTargetService } = await import("../services/kpi");
                const response = await createMonthlyTargetService(payload);
                return response;
            } catch (err) {
                console.error("Error creating monthly target", err);
                throw err;
            } finally {
                this.setLoading(false);
            }
        },
        async updateMonthlyTarget({ id, payload }) {
            this.setLoading(true);
            try {
                const { updateMonthlyTargetService } = await import("../services/kpi");
                const response = await updateMonthlyTargetService(id, payload);
                return response;
            } catch (err) {
                console.error("Error updating monthly target", err);
                throw err;
            } finally {
                this.setLoading(false);
            }
        },
        async deleteMonthlyTarget(id) {
            this.setLoading(true);
            try {
                const { deleteMonthlyTargetService } = await import("../services/kpi");
                const response = await deleteMonthlyTargetService(id);
                return response;
            } catch (err) {
                console.error("Error deleting monthly target", err);
                throw err;
            } finally {
                this.setLoading(false);
            }
        },
        async getRecurringDetail(payload) {
            const id = typeof payload === 'object' ? payload.id : payload;
            const hideLoading = typeof payload === 'object' ? payload.hideLoading : false;
            if (!hideLoading) this.setLoading(true);
            try {
                const { getRecurringDetailService } = await import("../services/kpi");
                const response = await getRecurringDetailService(id);
                if (response?.data) {
                    this.setRecurringDetail(response.data.data || response.data);
                    return response.data;
                }
            } catch (err) {
                console.error("Error fetching recurring detail", err);
            } finally {
                if (!hideLoading) this.setLoading(false);
            }
        },
        async updateRecurringStatus({ id, status }) {
            this.setLoading(true);
            try {
                const { updateRecurringStatusService } = await import("../services/kpi");
                const response = await updateRecurringStatusService(id, { status });
                this.getRecurringDetail({ id, hideLoading: true });
                return response;
            } catch (err) {
                console.error("Error updating recurring schedule status", err);
                throw err;
            } finally {
                this.setLoading(false);
            }
        },
        async editRecurringSchedule({ id, payload }) {
            this.setLoading(true);
            try {
                const { editRecurringScheduleService } = await import("../services/kpi");
                const response = await editRecurringScheduleService(id, payload);
                this.getRecurringDetail({ id, hideLoading: true });
                return response;
            } catch (err) {
                console.error("Error editing recurring schedule", err);
                throw err;
            } finally {
                this.setLoading(false);
            }
        }
    })
};
